// @ts-nocheck

import scrollIntoView from 'scroll-into-view-if-needed';

import { Target } from '../client';
import { getAppDOMElement, querySelector } from '../DOMElements';

import { easeInOutCubic, lerp } from './number';

export const SMOOTH_SCROLL_TIMEOUT = 750; // ms

const scrollToAnchor = (anchorId: string, behavior: string, offset?: number): HTMLElement | null => {
  if (anchorId) {
    const node = querySelector(`*[data-anchorid=${anchorId}]`);

    if (node) {
      if (offset) {
        const nodeStyle = window.getComputedStyle(node);
        const topValue = nodeStyle.getPropertyValue('top');

        node.style.top = `calc(${topValue} - ${offset}px)`;
      }

      scrollIntoView(node, {
        behavior,
        block: 'start',
        inline: 'center',
      });

      return node;
    }
  }

  return null;
};

export const smoothScrollToAnchor = (anchorId: string, offset?: number): HTMLElement | null => {
  return scrollToAnchor(anchorId, 'smooth', offset);
};

export const instantScrollToAnchor = (anchorId: string, offset?: number): HTMLElement | null => {
  return scrollToAnchor(anchorId, 'instant', offset);
};

/* Scroll to an element (targetNode) rendered inside a scrollable div (parentNode) */
export const smoothScrollToAnchorByParent = async (
  targetNode: HTMLElement,
  parentNode: HTMLElement,
  timeout?: number = 750,
  offset?: number = 10,
): Promise<HTMLElement | undefined> => {
  return new Promise((resolve, reject) => {
    if (targetNode && parentNode) {
      const startTime = performance.now();

      const initParentScrollTop = parentNode.scrollTop;
      const parentRect = parentNode.getBoundingClientRect();
      const targetRect = targetNode.getBoundingClientRect();
      const { top: pt } = parentRect;
      const { top: tt } = targetRect;
      const rtt = tt - pt; // parent relative target top
      const isAbove = rtt < 0; // is target above parent
      const isBelow = rtt > 0; // is target below parent
      const isVisible = !isAbove && !isBelow; // is target initially visible

      const scrollRAF = () => {
        const elapsedTime = performance.now() - startTime;
        const progressRatio = elapsedTime / timeout;

        if (progressRatio < 1) {
          if (isAbove) {
            parentNode.scrollTop = lerp(
              initParentScrollTop,
              initParentScrollTop - Math.abs(pt) - Math.abs(tt) - offset,
              easeInOutCubic(progressRatio),
            );
          }

          if (isBelow) {
            parentNode.scrollTop = lerp(
              initParentScrollTop,
              initParentScrollTop - Math.abs(pt) + Math.abs(tt) + offset,
              easeInOutCubic(progressRatio),
            );
          }

          requestAnimationFrame(scrollRAF);
        } else {
          resolve(targetNode);
        }
      };

      if (!isVisible) {
        requestAnimationFrame(scrollRAF);
      } else {
        resolve(targetNode);
      }
    } else {
      reject();
    }
  });
};

/* Scroll top to a position inside a scrollable div (node) */
export const smoothScrollToPosition = async (
  position: number,
  node: HTMLElement,
  timeout?: number = 750,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      if (node) {
        const startTime = performance.now();

        const initParentScrollTop = node.scrollTop;
        const parentRect = node.getBoundingClientRect();
        const { top: pt } = parentRect;

        const scrollRAF = () => {
          const elapsedTime = performance.now() - startTime;
          const progressRatio = elapsedTime / timeout;

          if (progressRatio < 1) {
            node.scrollTop = lerp(initParentScrollTop, position, easeInOutCubic(progressRatio));

            requestAnimationFrame(scrollRAF);
          } else {
            resolve();
          }
        };

        if (pt !== position) {
          requestAnimationFrame(scrollRAF);
        } else {
          resolve();
        }
      } else {
        reject();
      }
    } catch {
      node.scrollTo({ top: position, behavior: 'smooth' });
      resolve();
    }
  });
};

export const smartScrollToRootAnchor = async (anchorId: string, timeout: number, offset?: number) => {
  if (timeout === 0) {
    return scrollToAnchor(anchorId, 'instant', offset);
  }
  if (Target.__MOBILE__) {
    const anchorEl = querySelector(`*[data-anchorid=${anchorId}]`);
    const parentEl = getAppDOMElement();
    if (anchorEl && parentEl) {
      return smoothScrollToAnchorByParent(anchorEl, parentEl, timeout, offset);
    }
    return scrollToAnchor(anchorId, 'instant', offset);
  } else {
    return scrollToAnchor(anchorId, 'smooth', offset);
  }
};
